import { CompoundInterestInput } from './calculators/compoundInterest';
import { RentalPropertyInput } from './calculators/rentalProperty';
import {
  COMPOUND_RANGES, RENTAL_RANGES, CompoundingFrequency, BundeslandCode, BUNDESLAENDER, TAX_PRESETS,
  getKirchensteuerRateForBundesland,
} from './calculatorConfig';
import { getStorageConsent, setStorageConsent, StorageChoice } from './storageConsent';

export interface AppState {
  activeCalculator: 'compound' | 'rental';
  locale: 'en' | 'de';
  theme: 'light' | 'dark';
  bundesland: BundeslandCode;
  compound: CompoundInterestInput;
  rental: RentalPropertyInput;
}

type DeepPartial<T> = T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } : T;

const DEFAULT_STATE: AppState = {
  activeCalculator: 'compound',
  locale: 'de',
  theme: 'dark',
  bundesland: 'NW',
  compound: {
    initialInvestment: COMPOUND_RANGES.initialInvestment.default,
    monthlyContribution: COMPOUND_RANGES.monthlyContribution.default,
    annualInterestRate: COMPOUND_RANGES.annualInterestRate.default,
    years: COMPOUND_RANGES.years.default,
    compoundingFrequency: CompoundingFrequency.Monthly,
    inflationRate: COMPOUND_RANGES.inflationRate.default,
    filingStatus: 'single',
    taxYear: 2026,
    kirchensteuerRate: 0,
  },
  rental: {
    objectName: '',
    purchasePrice: RENTAL_RANGES.purchasePrice.default,
    monthlyRent: RENTAL_RANGES.monthlyRent.default,
    buildingYear: RENTAL_RANGES.buildingYear.default,
    hausgeldMonthly: RENTAL_RANGES.hausgeldMonthly.default,
    maintenanceReserveMonthly: RENTAL_RANGES.maintenanceReserveMonthly.default,
    tenantServiceChargesMonthly: RENTAL_RANGES.tenantServiceChargesMonthly.default,
    equity: RENTAL_RANGES.equity.default,
    interestRate: RENTAL_RANGES.interestRate.default,
    tilgungRate: RENTAL_RANGES.tilgungRate.default,
    taxableIncome: RENTAL_RANGES.taxableIncome.default,
    filingStatus: 'single',
    kirchensteuerRate: 0,
    taxYear: 2026,
    notaryRate: RENTAL_RANGES.notaryRate.default,
    brokerRate: RENTAL_RANGES.brokerRate.default,
    buildingShare: RENTAL_RANGES.buildingShare.default,
    afaMethod: 'linear',
    zinsbindungYears: RENTAL_RANGES.zinsbindungYears.default,
    rentGrowth: RENTAL_RANGES.rentGrowth.default,
    valueGrowth: RENTAL_RANGES.valueGrowth.default,
    vacancyRate: RENTAL_RANGES.vacancyRate.default,
    holdingYears: RENTAL_RANGES.holdingYears.default,
  },
};

// v3: the rental calculator's cost inputs changed (Hausgeld / reserve / tenant service charges),
// so older saved states are not reused.
const LS_KEY = 'gfc_state_v3';

// Short URL keys for non-default values
const URL_KEYS: Record<string, string> = {
  'compound.initialInvestment':   'ci',
  'compound.monthlyContribution': 'cm',
  'compound.annualInterestRate':  'cr',
  'compound.years':               'cy',
  'compound.compoundingFrequency':'cf',
  'compound.inflationRate':       'cinf',
  'compound.filingStatus':        'cfs',
  'compound.taxYear':             'cty',
  'compound.kirchensteuerRate':   'ck',
  'rental.objectName':                 'ro',
  'rental.purchasePrice':              'rp',
  'rental.monthlyRent':                'ri',
  'rental.buildingYear':               'rby',
  'rental.hausgeldMonthly':            'rhg',
  'rental.maintenanceReserveMonthly':  'rmr',
  'rental.tenantServiceChargesMonthly':'rtc',
  'rental.equity':                     'req',
  'rental.interestRate':               'rr',
  'rental.tilgungRate':                'rtp',
  'rental.taxableIncome':              'rti',
  'rental.filingStatus':               'rfs',
  'rental.kirchensteuerRate':          'rk',
  'rental.taxYear':                    'rty',
  'rental.notaryRate':                 'rnr',
  'rental.brokerRate':                 'rbr',
  'rental.buildingShare':              'rbs',
  'rental.afaMethod':                  'ram',
  'rental.zinsbindungYears':           'rzb',
  'rental.rentGrowth':                 'rrg',
  'rental.valueGrowth':                'rvg',
  'rental.vacancyRate':                'rvr',
  'rental.holdingYears':               'rhy',
  'bundesland':                   'bl',
  'activeCalculator':             'tab',
};

const URL_KEYS_INV: Record<string, string> = Object.fromEntries(
  Object.entries(URL_KEYS).map(([k, v]) => [v, k])
);

let state: AppState = structuredClone(DEFAULT_STATE);

/**
 * Saved states and shared links are untrusted input: bring every rental value back into its
 * allowed range and reset anything that is not a valid choice, so a stale or hand-edited
 * link can never feed nonsense into the calculation.
 */
function sanitize(s: AppState): AppState {
  const defaults = DEFAULT_STATE.rental;
  const r = { ...s.rental } as Record<string, unknown>;
  for (const [key, range] of Object.entries(RENTAL_RANGES)) {
    const v = r[key];
    r[key] = typeof v === 'number' && isFinite(v)
      ? Math.max(range.min, Math.min(range.max, v))
      : range.default;
  }
  for (const key of ['buildingYear', 'zinsbindungYears', 'holdingYears']) {
    r[key] = Math.round(r[key] as number);
  }
  if (r['filingStatus'] !== 'single' && r['filingStatus'] !== 'married') r['filingStatus'] = defaults.filingStatus;
  if (r['afaMethod'] !== 'linear' && r['afaMethod'] !== 'degressive') r['afaMethod'] = defaults.afaMethod;
  if (![0, 0.08, 0.09].includes(r['kirchensteuerRate'] as number)) r['kirchensteuerRate'] = 0;
  if (!(String(r['taxYear']) in TAX_PRESETS)) r['taxYear'] = defaults.taxYear;
  r['objectName'] = typeof r['objectName'] === 'string' ? (r['objectName'] as string).slice(0, 80) : '';

  const bundesland = BUNDESLAENDER.some((b) => b.code === s.bundesland) ? s.bundesland : DEFAULT_STATE.bundesland;
  // The church tax rate is a property of the state, so a link can only switch it on or off.
  const churchRate = getKirchensteuerRateForBundesland(bundesland);
  if (r['kirchensteuerRate'] !== 0) r['kirchensteuerRate'] = churchRate;
  const compound = { ...s.compound, kirchensteuerRate: s.compound.kirchensteuerRate !== 0 ? churchRate : 0 } as const;
  return { ...s, bundesland, compound, rental: r as unknown as RentalPropertyInput };
}
const listeners = new Set<() => void>();

function deepMerge<T>(target: T, partial: DeepPartial<T>): T {
  const result = { ...target };
  for (const key in partial) {
    const val = partial[key as keyof T];
    if (val !== undefined && val !== null && typeof val === 'object' && !Array.isArray(val)) {
      (result as Record<string, unknown>)[key] = deepMerge(
        (target as Record<string, unknown>)[key] as object,
        val as DeepPartial<object>
      );
    } else if (val !== undefined) {
      (result as Record<string, unknown>)[key] = val;
    }
  }
  return result;
}

export function getState(): AppState {
  return state;
}

export function setState(partial: DeepPartial<AppState>): void {
  state = deepMerge(state, partial);
  listeners.forEach((fn) => fn());
}

/**
 * Select the federal state. It drives the church tax rate (only for calculators where church
 * tax is switched on) and the real estate transfer tax in the rental calculator.
 */
export function setBundesland(code: BundeslandCode): void {
  const rate = getKirchensteuerRateForBundesland(code);
  setState({
    bundesland: code,
    compound: { kirchensteuerRate: state.compound.kirchensteuerRate !== 0 ? rate : 0 },
    rental: { kirchensteuerRate: state.rental.kirchensteuerRate !== 0 ? rate : 0 },
  });
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Keys used by earlier versions of the app; removed when the visitor declines storage. */
const LEGACY_LS_KEYS = ['gfc_state_v1', 'gfc_state_v2'];

/** Saves the inputs in the browser, but only if the visitor allowed it in the storage banner. */
export function saveToLocalStorage(): void {
  if (getStorageConsent() !== 'granted') return;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    // quota exceeded — silently ignore
  }
}

/** Deletes every saved copy of the inputs (current and older versions). */
export function clearSavedState(): void {
  for (const key of [LS_KEY, ...LEGACY_LS_KEYS]) {
    try {
      localStorage.removeItem(key);
    } catch {
      // storage blocked — nothing to delete
    }
  }
}

/**
 * Applies the visitor's storage choice: "granted" saves the current inputs straight away,
 * "denied" deletes anything saved before. The app keeps working in memory either way.
 */
export function applyStorageChoice(choice: StorageChoice): void {
  setStorageConsent(choice);
  if (choice === 'granted') saveToLocalStorage();
  else clearSavedState();
}

export function loadFromLocalStorage(): void {
  if (getStorageConsent() !== 'granted') return;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as DeepPartial<AppState>;
    state = sanitize(deepMerge(DEFAULT_STATE, parsed));
  } catch {
    // corrupted — ignore
  }
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i] as string;
    if (typeof cur[key] !== 'object') cur[key] = {};
    cur = cur[key] as Record<string, unknown>;
  }
  const lastKey = parts[parts.length - 1] as string;
  cur[lastKey] = value;
}

export function encodeToURL(): string {
  const params = new URLSearchParams();
  const defaults = DEFAULT_STATE as unknown as Record<string, unknown>;
  for (const [path, shortKey] of Object.entries(URL_KEYS)) {
    const current = getNestedValue(state as unknown as Record<string, unknown>, path);
    const def = getNestedValue(defaults, path);
    if (current !== def) {
      params.set(shortKey, String(current));
    }
  }
  const base = `${window.location.origin}${window.location.pathname}`;
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function decodeFromURL(): void {
  const params = new URLSearchParams(window.location.search);
  if (params.size === 0) return;

  const partial: Record<string, unknown> = {};
  params.forEach((value, shortKey) => {
    const path = URL_KEYS_INV[shortKey];
    if (!path) return;
    // Determine type from DEFAULT_STATE
    const def = getNestedValue(DEFAULT_STATE as unknown as Record<string, unknown>, path);
    let parsed: unknown;
    if (typeof def === 'number') {
      parsed = parseFloat(value);
      if (isNaN(parsed as number)) return;
    } else if (typeof def === 'string') {
      parsed = value;
    } else if (typeof def === 'boolean') {
      parsed = value === 'true';
    } else {
      parsed = value;
    }
    setNestedValue(partial, path, parsed);
  });
  state = sanitize(deepMerge(state, partial as DeepPartial<AppState>));
}

export function getDefaultState(): AppState {
  return structuredClone(DEFAULT_STATE);
}
